// src/context/LeagueDataContext.jsx
//
// Focused context for raw league data, loading state, and data-load actions.
//
import React, { createContext, useState, useContext, useMemo } from 'react';
import { fetchLeagueData, triggerSeed, abortSeed, getSeedStatus } from '../data/dataProcessing';

const LeagueDataContext = createContext(null);

export const useLeagueData = () => {
  const context = useContext(LeagueDataContext);
  if (!context) throw new Error('useLeagueData must be used within a LeagueDataProvider');
  return context;
};

export const LeagueDataProvider = ({ children }) => {
  const [data,      setData]      = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState(null);

  // Load the pre-computed analysis blob from the API.
  // Only call this once status === 'complete'.
  const loadFromAPI = async (leagueId) => {
    setIsLoading(true);
    setError(null);
    try {
      const parsed = await fetchLeagueData(leagueId);
      setData(parsed);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Error loading league data:', err);
      const msg = err.name === 'AbortError'
        ? 'Loading timed out — the data may be large. Retrying…'
        : err.message || 'Failed to load league data — try again shortly.';
      setError(msg);
      setIsLoading(false);
      return false;
    }
  };

  // Trigger a background seed for the given league ID.
  // Returns the server response ({ status, league_id }).
  const startSeed = async (leagueId, clientId) => {
    try {
      return await triggerSeed(leagueId, clientId);
    } catch (err) {
      console.error('Seed trigger failed:', err);
      return null;
    }
  };

  // Abort a running seed (called on league ID change).
  const cancelSeed = async (leagueId) => {
    await abortSeed(leagueId);
  };

  // Poll status endpoint — returns the status object.
  const pollStatus = async (leagueId) => {
    return getSeedStatus(leagueId);
  };

  // Clear loaded data (e.g. when user navigates back to landing).
  const clearData = () => {
    setData(null);
    setError(null);
  };

  const value = useMemo(() => ({
    data,
    isLoading,
    error,
    loadFromAPI,
    startSeed,
    cancelSeed,
    pollStatus,
    clearData,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [data, isLoading, error]);

  return (
    <LeagueDataContext.Provider value={value}>
      {children}
    </LeagueDataContext.Provider>
  );
};

export default LeagueDataContext;
